import { CircularProgress } from '@mui/material'
import { DataTable } from 'components/Shared/DataTable'
import { type GridColDef } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import {
  type BadgeCategory,
  type BadgeRewardRenewableType,
  type BadgeRewardType,
} from 'src/types/api'
import { dataTablePromotionDateFormatter } from 'utils/util'
import { useGetAllLocationBadges } from 'hooks/api/Badges/useGetAllLocationBadges'
import { useGetMe } from 'hooks/api/useGetMe'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { getLEIdsByPermission } from 'utils/permissions'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'

const getColumns = (category?: BadgeCategory): GridColDef[] => [
  { field: 'id', headerName: 'ID', minWidth: 25 },
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 150,
    flex: 1,
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 1,
  },
  {
    field: 'type',
    headerName: 'Type',
    minWidth: 250,
    flex: 1,
    valueGetter: (params) => {
      const badgeType = (type: BadgeRewardType) => {
        switch (type) {
          case 'CheckIn':
            return 'Check-In'
          case 'CheckInStreak':
            return 'Check-In Streak'
          case 'PatronProfile_Anniversary':
            return 'Patron Profile - Anniversary'
          case 'Referral_Count':
            return 'Referral - Count'
          case 'PatronProfile_Address':
            return 'Patron Profile - Address'
          case 'PatronProfile_Birthday':
            return 'Patron Profile - Birthday'
          case 'LocationCheckIn':
            return 'Location Check-In'
          case 'LocationCheckInStreak':
            return 'Location Check-In Streak'
          case 'LocationUniqueCheckIn':
            return 'Unique Location Check-In'
          case 'UniqueLocationVisits':
            return 'Unique Location Visits'
          default:
            return ''
        }
      }

      const badgeRewardType = params.row.type as BadgeRewardType
      return badgeType(badgeRewardType)
    },
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    minWidth: 100,
    flex: 1,
    valueFormatter: dataTablePromotionDateFormatter(false, 'Start'),
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    minWidth: 100,
    flex: 1,
    valueFormatter: dataTablePromotionDateFormatter(false, 'End'),
  },
  {
    field: 'renewableType',
    headerName: 'Renewable',
    minWidth: 150,
    flex: 1,
    valueGetter: (params) => {
      const renewableType = (typ: BadgeRewardRenewableType) => {
        switch (typ) {
          case 'Daily':
            return 'Daily'
          case 'Weekly':
            return 'Weekly'
          case 'Monthly':
            return 'Monthly'
          case 'Yearly':
            return 'Yearly'
          case 'CheckInStreak':
            return 'Check-In Streak'
          default:
            return 'No'
        }
      }

      const leRenewableType = params.row
        .renewableType as BadgeRewardRenewableType
      return renewableType(leRenewableType)
    },
  },
  {
    field: 'dependentBadgeRewardId',
    headerName: 'Depends On Badge Id',
    minWidth: 200,
    flex: 1,
    valueGetter: (params) => {
      return params.row.dependentBadgeRewardId === 0
        ? ''
        : params.row.dependentBadgeRewardId
    },
  },
  {
    field:
      category === 'LocationBadge'
        ? 'locationPointsAwarded'
        : 'jjPointsAwarded',
    headerName:
      category === 'LocationBadge'
        ? 'Location Points to award'
        : 'JJ Points to award',
    minWidth: 175,
    flex: 1,
    valueGetter: (params) => {
      const pointsAwarded =
        category === 'LocationBadge'
          ? params.row.locationPointsAwarded
          : params.row.jjPointsAwarded

      return pointsAwarded === 0 ? '' : pointsAwarded
    },
  },
]

export const BadgesTable = ({ category }: { category: BadgeCategory }) => {
  const navigate = useNavigate()
  const query = useGetAllLocationBadges()
  const meQuery = useGetMe()
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'badgeLocationsPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = query.data?.filter(
    (x) =>
      x.licensedEstablishmentIds?.some((id) => leIdsByPermission.includes(id))
  )

  const rows = licensedEstablishmentsFiltered?.filter(
    (badgeReward) => badgeReward.category === category
  )

  const columns = getColumns(category)

  if (query.isPending || meQuery.isPending) {
    return <CircularProgress />
  }

  if (query.isError || meQuery.isError) {
    return <p>An error occured.</p>
  }

  if (leIdsByPermission.length === 0) {
    return (
      <NoDataIndicator
        noImage
        noDataMessage="Your current permissions do not allow access to view location badges. To obtain access, please contact your Account Manager for assistance."
      />
    )
  }

  return (
    <DataTable
      getRowClassName={() => 'text-[#2c2c27]'}
      data-testid="badge-rewards-table"
      columns={columns}
      rows={rows ?? []}
      onRowClick={async (params) => {
        await navigate(`/LocationBadges/${params.id}`)
      }}
      getRowHeight={() => 'auto'}
      sx={{
        '& .MuiDataGrid-cell': {
          alignItems: 'flex-start',
          lineHeight: 1.4,
          py: 1.5,
        },
        '& .MuiDataGrid-cellContent': {
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'normal',
        },
      }}
    />
  )
}
